import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { type PagedResult, readApiETag } from './types';

/**
 * Listagem e criação de Empresa / Originadora / Pessoa para o combobox "buscar ou criar nova" do
 * +Novo Deal (E6 PR2). Na escala da Vega (poucas centenas) carregamos a lista inteira e filtramos no
 * cliente — mesmo padrão client-side já adotado no kanban (sessão 13); evita busca server-side (E7)
 * como dependência. As taxonomias (setor/tipo) vêm do GET /reference (useReferenceData).
 *
 * "Lista inteira" = TODAS as páginas (`fetchAllEntityPages`): o backend limita cada página a
 * MaxTake=200 (backend/PagedResult.cs) e ordena por nome, então uma única página cortava a base em
 * ordem alfabética (bug de 2026-10-08: empresas a partir de ~"R", ex. "Rumos", sumiam do picker).
 */

const LIST_TAKE = 200; // = MaxTake do backend
// Teto de páginas: nunca mais que MAX_PAGES * LIST_TAKE = 2.000 registros por entidade. Guarda contra
// `total` vir estranho (bug de servidor) virando loop de fato ilimitado — mesmo teto do useDeals.
const MAX_PAGES = 10;

/**
 * Busca TODAS as páginas de uma listagem paginada (`/api/companies`, `/api/originators`,
 * `/api/people`). Página 1 dá o `total`; as restantes (skip=200,400,...) vão em paralelo. Dedupe por
 * id: se a base mudar entre a 1ª página e as seguintes, a janela de skip pode se sobrepor. Espelha o
 * `fetchAllPages` de useDeals.ts. Devolve o mesmo formato PagedResult (consumidores intocados).
 */
export async function fetchAllEntityPages<T extends { id: string }>(path: string): Promise<PagedResult<T>> {
  const first = await apiFetch<PagedResult<T>>(`${path}?take=${LIST_TAKE}`);
  const totalPages = Math.min(Math.ceil(first.total / LIST_TAKE), MAX_PAGES);
  if (totalPages <= 1) return first;

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) =>
      apiFetch<PagedResult<T>>(`${path}?take=${LIST_TAKE}&skip=${(i + 1) * LIST_TAKE}`),
    ),
  );

  const seen = new Set<string>();
  const items: T[] = [];
  for (const page of [first, ...rest]) {
    for (const item of page.items) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      items.push(item);
    }
  }
  return { items, total: first.total, skip: 0, take: items.length };
}

// staleTime 0: sempre refetch ao (re)habilitar — evita servir lista velha (ex.: empresa
// criada inline) quando o consumidor reabre com dados já em cache.
const STALE = 0;

export interface CompanyListItem {
  id: string;
  name: string;
  sectorId: number;
}
export interface OriginatorListItem {
  id: string;
  name: string;
  typeId: number;
  vegaRelationshipManagerId: string | null;
}
export interface PersonListItem {
  id: string;
  name: string;
  email: string | null;
  currentOriginatorId: string | null;
}

export const COMPANIES_QUERY_KEY = ['companies'] as const;
export const ORIGINATORS_QUERY_KEY = ['originators'] as const;
export const PEOPLE_QUERY_KEY = ['people'] as const;

// `enabled` permite gatear o fetch (ex.: só quando o modal de +Novo Deal está aberto) — evita
// disparar as 3 listas em todo page load (e bater no Function App frio à toa).
export function useCompanies(enabled = true) {
  return useQuery({
    queryKey: COMPANIES_QUERY_KEY,
    queryFn: () => fetchAllEntityPages<CompanyListItem>('/api/companies'),
    staleTime: STALE,
    enabled,
  });
}

export function useOriginators(enabled = true) {
  return useQuery({
    queryKey: ORIGINATORS_QUERY_KEY,
    queryFn: () => fetchAllEntityPages<OriginatorListItem>('/api/originators'),
    staleTime: STALE,
    enabled,
  });
}

export function usePeople(enabled = true) {
  return useQuery({
    queryKey: PEOPLE_QUERY_KEY,
    queryFn: () => fetchAllEntityPages<PersonListItem>('/api/people'),
    staleTime: STALE,
    enabled,
  });
}

/** Resposta mínima de criação que o combobox consome (id + name). */
interface CreatedEntity {
  id: string;
  name: string;
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; sectorId: number }) =>
      apiFetch<CreatedEntity>('/api/companies', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY }),
  });
}

export function useCreateOriginator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      typeId: number;
      vegaRelationshipManagerId?: string | null;
      summary?: string | null;
    }) => apiFetch<CreatedEntity>('/api/originators', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ORIGINATORS_QUERY_KEY }),
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      email?: string | null;
      phone?: string | null;
      currentOriginatorId?: string | null;
      role?: string | null;
      summary?: string | null;
    }) => apiFetch<CreatedEntity>('/api/people', { method: 'POST', body }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
  });
}

// ----- Edição de entidade na ficha do deal (Item 6, lote 3; admin-gated no frontend) -----
// Os list-items não trazem ETag → busca o atual p/ o If-Match (padrão do useUpdateTask) e
// preserva os campos que o mini-form não edita (originator: vegaRM/summary; person: phone/role/summary).
// Invalida a lista da entidade e ['deals'] (rótulos resolvidos no servidor refrescam no card/lista).

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string; sectorId: number }) => {
      // Achado F8: leitura tolerante do ETag (eTag/etag) — mesma cautela do readApiETag usado no
      // resto do domínio de Deal, para não depender de como o System.Text.Json serializa o acrônimo.
      const cur = await apiFetch<{ eTag?: string; etag?: string }>(`/api/companies/${input.id}`);
      return apiFetch<CreatedEntity>(`/api/companies/${input.id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${readApiETag(cur)}"` },
        body: { name: input.name, sectorId: input.sectorId },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: COMPANIES_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useUpdateOriginator() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; name: string; typeId: number }) => {
      const cur = await apiFetch<{
        eTag?: string;
        etag?: string;
        vegaRelationshipManagerId: string | null;
        summary: string | null;
      }>(`/api/originators/${input.id}`);
      return apiFetch<CreatedEntity>(`/api/originators/${input.id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${readApiETag(cur)}"` },
        body: {
          name: input.name,
          typeId: input.typeId,
          vegaRelationshipManagerId: cur.vegaRelationshipManagerId,
          summary: cur.summary,
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ORIGINATORS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useUpdatePerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name: string;
      email: string | null;
      currentOriginatorId: string | null;
    }) => {
      const cur = await apiFetch<{
        eTag?: string;
        etag?: string;
        phone: string | null;
        role: string | null;
        summary: string | null;
      }>(`/api/people/${input.id}`);
      return apiFetch<CreatedEntity>(`/api/people/${input.id}`, {
        method: 'PUT',
        headers: { 'If-Match': `"${readApiETag(cur)}"` },
        body: {
          name: input.name,
          email: input.email,
          phone: cur.phone,
          currentOriginatorId: input.currentOriginatorId,
          role: cur.role,
          summary: cur.summary,
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}
